import {cn} from '@sollapay/ui';
import {Skeleton, Typography} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {BALANCE_HOVER_MOTION_CLASS} from '../config';
import {getActiveBalance, getAmountSizeClass} from '../utils';

import type {TrustStatus} from '@sollapay/enums';
import type {FC} from 'react';

export interface TrustAccountBalancePanelProps {
  status: TrustStatus;
  /** `undefined` while balances load; `null` when the account has none. */
  balance: string | null | undefined;
}

/** The grid card's balance: a tinted "live" panel when money is held, a quiet one at ₪0 or none. */
export const TrustAccountBalancePanel: FC<TrustAccountBalancePanelProps> = ({status, balance}) => {
  const {t} = useTranslation('trustAccounts');
  const amount = getActiveBalance(status, balance);
  const isLive = amount?.isPositive() ?? false;
  const amountText = amount ? amount.formatNIS({symbol: true}) : '—';

  return (
    <div
      data-slot="trust-account-balance-panel"
      data-live={isLive || undefined}
      className={cn(
        'flex flex-col gap-2.5 rounded-lg p-4 transition-[background-color,box-shadow] duration-250 motion-reduce:transition-none',
        isLive
          ? 'bg-background-selected ring-1 ring-inset ring-border-subtle group-hover/row:bg-background-selected-strong group-hover/row:ring-brand-accent/20'
          : 'bg-background-secondary'
      )}
    >
      <span
        aria-hidden
        data-slot="bar"
        className={cn(
          'h-1 w-7 rounded-full transition-[width]',
          BALANCE_HOVER_MOTION_CLASS,
          isLive ? 'bg-progress-fill group-hover/row:w-12' : 'bg-border'
        )}
      />
      <div className="flex items-baseline justify-between gap-3">
        <Typography as="span" size="sm" color={isLive ? 'secondary' : 'tertiary'}>
          {t('columns.balance')}
        </Typography>
        {balance === undefined ? (
          <Skeleton data-slot="amount-skeleton" className="h-8 w-28" />
        ) : (
          <Typography
            as="span"
            dir="ltr"
            weight={isLive ? 'bold' : 'semibold'}
            className={cn(
              'inline-block shrink-0 overflow-visible font-display leading-tight tabular-nums transition-[scale,color] ltr:origin-right rtl:origin-left',
              getAmountSizeClass(amountText),
              BALANCE_HOVER_MOTION_CLASS,
              isLive
                ? 'text-fg group-hover/row:scale-104'
                : amount
                  ? 'text-fg-disabled group-hover/row:text-fg-tertiary'
                  : 'text-fg-disabled'
            )}
          >
            {amountText}
          </Typography>
        )}
      </div>
    </div>
  );
};
